import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft,
    CheckCircle2,
    GraduationCap,
    BookOpen,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { submitCounselingLead } from "../lib/api";

// ── Validation ─────────────────────────────────────────────────────────────────
const req = (v) => v != null && String(v).trim() !== "";
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
const normalizePhone = (v) => {
    let d = String(v || "").replace(/\D/g, "");
    if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
    if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
    return d;
};
const isMobile = (v) => /^[6-9]\d{9}$/.test(normalizePhone(v));
const isName = (v) => /^[A-Za-z][A-Za-z .'-]{1,}$/.test(String(v || "").trim());

const TWELFTH_OPTIONS = [
    { value: "Completed", label: "Yes, Completed" },
    { value: "Currently Studying", label: "Currently in 12th" },
    { value: "Not Yet", label: "Not Yet (Below 12th)" },
];

// ── Reusable text field ────────────────────────────────────────────────────────
function TextField({ label, name, form, set, errors, placeholder, type = "text", required }) {
    const error = errors[name];
    return (
        <div>
            <Label className="text-sm font-medium text-gray-700">
                {label} {required && <span className="text-red-500">*</span>}
            </Label>
            <Input
                type={type}
                value={form[name] ?? ""}
                onChange={(e) => set(name, e.target.value)}
                placeholder={placeholder}
                aria-invalid={!!error}
                className={`mt-1.5 h-12 rounded-xl ${error ? "border-red-500 focus-visible:ring-red-500" : ""}`}
            />
            {error && <p className="mt-1 text-xs font-medium text-red-500">{error}</p>}
        </div>
    );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function CounselingPage() {
    const navigate = useNavigate();

    const [form, setFormState] = useState({
        full_name: "",
        mobile_number: "",
        email: "",
        college_name: "",
        twelfth_status: "",
        attend_nalgonda_session: "",
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const set = (name, value) => {
        setFormState((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    function validate() {
        const errs = {};
        if (!req(form.full_name)) errs.full_name = "Full name is required";
        else if (!isName(form.full_name)) errs.full_name = "Enter a valid name (letters only)";
        if (!req(form.mobile_number)) errs.mobile_number = "Mobile number is required";
        else if (!isMobile(form.mobile_number)) errs.mobile_number = "Enter a valid 10-digit Indian mobile number";
        if (req(form.email) && !isEmail(form.email)) errs.email = "Enter a valid email address";
        if (!req(form.twelfth_status)) errs.twelfth_status = "Please select your 12th status";
        if (!req(form.attend_nalgonda_session)) errs.attend_nalgonda_session = "Please select Yes or No";
        return errs;
    }

    async function handleSubmit() {
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            toast.error("Please fix the highlighted fields before submitting.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                full_name: form.full_name.trim(),
                mobile_number: normalizePhone(form.mobile_number),
                email: form.email.trim().toLowerCase() || undefined,
                college_name: form.college_name.trim() || undefined,
                twelfth_status: form.twelfth_status,
                attend_nalgonda_session: form.attend_nalgonda_session,
            };
            await submitCounselingLead(payload);
            setSuccess(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            if (err.alreadyRegistered) {
                toast.info(err.message || "You've already submitted. Our team will reach out soon.");
            } else {
                toast.error(err.message || "Something went wrong. Please try again.");
            }
        } finally {
            setSubmitting(false);
        }
    }

    // ── Success screen ───────────────────────────────────────────────────────────
    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center"
                >
                    <div className="flex justify-center mb-6">
                        <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle2 className="h-10 w-10 text-green-500" />
                        </div>
                    </div>
                    <h1 className="text-2xl font-black text-gray-900 mb-3">Request Received!</h1>
                    <p className="text-gray-600 mb-2">
                        Thanks, <strong>{form.full_name}</strong>! Your counseling request has been submitted.
                    </p>
                    <p className="text-sm text-gray-500 mb-8">
                        {form.attend_nalgonda_session === "Yes"
                            ? "See you at the Nalgonda counseling session! Our team will confirm the details with you shortly."
                            : "Thank you for filling the form. Our team will reach out to you soon."}
                    </p>
                    <Button
                        onClick={() => navigate("/")}
                        className="w-full rounded-full bg-aiblue hover:bg-blue-700 text-white font-semibold py-5"
                    >
                        Back to Home
                    </Button>
                </motion.div>
            </div>
        );
    }

    // ── Form ─────────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 pt-24 pb-16 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Back link */}
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 mb-8 transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" /> Back to Home
                </Link>

                {/* Header */}
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                    <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                        Free Counseling Session
                    </div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl text-gray-900 leading-tight">
                        Get Free Career Counseling
                    </h1>
                    <p className="mt-3 text-gray-500">
                        Fill in your details and one of our expert counselors will reach out to help you choose the right B.Tech specialization for your future.
                    </p>
                </motion.div>

                {/* Form card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 }}
                    className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden"
                >
                    {/* Section 1 — Personal Details */}
                    <div className="p-8 pb-6 border-b border-gray-100">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <GraduationCap className="h-4 w-4 text-blue-600" />
                            </div>
                            <h2 className="font-bold text-gray-900">Personal Details</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <TextField
                                label="Full Name" name="full_name"
                                form={form} set={set} errors={errors}
                                placeholder="Arjun Sharma" required
                            />
                            <TextField
                                label="Mobile Number" name="mobile_number" type="tel"
                                form={form} set={set} errors={errors}
                                placeholder="9876543210" required
                            />
                            <TextField
                                label="Email ID (optional)" name="email" type="email"
                                form={form} set={set} errors={errors}
                                placeholder="arjun@example.com"
                            />
                            <TextField
                                label="College / School Name (optional)" name="college_name"
                                form={form} set={set} errors={errors}
                                placeholder="e.g. Sri Chaitanya, Nalgonda"
                            />
                        </div>
                    </div>

                    {/* Section 2 — Education Details */}
                    <div className="p-8 pb-6 border-b border-gray-100">
                        <div className="flex items-center gap-2 mb-6">
                            <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
                                <BookOpen className="h-4 w-4 text-orange-500" />
                            </div>
                            <h2 className="font-bold text-gray-900">Education Details</h2>
                        </div>
                        <Label className="text-sm font-medium text-gray-700 mb-2 block">
                            Have you completed 12th? <span className="text-red-500">*</span>
                        </Label>
                        <div className="grid grid-cols-3 gap-2">
                            {TWELFTH_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => set("twelfth_status", opt.value)}
                                    className={`min-h-[44px] py-2 px-2 rounded-xl border-2 text-xs font-semibold leading-tight transition-all ${
                                        form.twelfth_status === opt.value
                                            ? "border-aiblue bg-blue-50 text-aiblue"
                                            : "border-gray-200 bg-white text-gray-600 hover:border-blue-300"
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        {errors.twelfth_status && (
                            <p className="mt-1.5 text-xs font-medium text-red-500">{errors.twelfth_status}</p>
                        )}
                    </div>

                    {/* Section 3 — Nalgonda Session */}
                    <div className="p-8 pb-6 border-b border-gray-100">
                        <div className="flex items-start gap-3 mb-5 p-4 bg-blue-50 rounded-2xl border border-blue-100">
                            <span className="text-2xl mt-0.5">📍</span>
                            <div>
                                <p className="font-bold text-gray-900 text-sm">Counseling Session — Nalgonda IT Tower</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    We are hosting an in-person free counseling session in <strong>Nalgonda IT Tower</strong>. Would you like to attend?
                                </p>
                            </div>
                        </div>
                        <Label className="text-sm font-medium text-gray-700 mb-3 block">
                            Are you interested in attending? <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex gap-4">
                            {["Yes", "No"].map((opt) => (
                                <button
                                    key={opt}
                                    type="button"
                                    onClick={() => set("attend_nalgonda_session", opt)}
                                    className={`flex-1 h-12 rounded-xl border-2 text-sm font-bold transition-all ${
                                        form.attend_nalgonda_session === opt
                                            ? opt === "Yes"
                                                ? "border-green-500 bg-green-50 text-green-700"
                                                : "border-red-400 bg-red-50 text-red-600"
                                            : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                                    }`}
                                >
                                    {opt === "Yes" ? "✓ Yes, I'm interested" : "✗ No, not this time"}
                                </button>
                            ))}
                        </div>
                        {errors.attend_nalgonda_session && (
                            <p className="mt-1.5 text-xs font-medium text-red-500">{errors.attend_nalgonda_session}</p>
                        )}
                    </div>

                    {/* Submit */}
                    <div className="px-8 py-8">
                        <Button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="w-full rounded-2xl bg-aiblue hover:bg-blue-700 text-white font-bold py-6 text-base shadow-lg shadow-blue-200 transition-all"
                        >
                            {submitting ? "Submitting…" : "Register Your Seat "}
                        </Button>
                        <p className="text-center text-xs text-gray-400 mt-4">
                            Our team will call you within <strong>24 hours</strong>. No spam, ever.
                        </p>
                    </div>
                </motion.div>

                {/* Trust badges */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="mt-8 grid grid-cols-3 gap-4 text-center"
                >
                    {[
                        { icon: "🎓", text: "Expert Counselors" },
                        { icon: "⚡", text: "24h Response" },
                        { icon: "🔒", text: "100% Free" },
                    ].map((item) => (
                        <div key={item.text} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                            <div className="text-2xl mb-1">{item.icon}</div>
                            <p className="text-xs font-semibold text-gray-600">{item.text}</p>
                        </div>
                    ))}
                </motion.div>
            </div>
        </div>
    );
}
